import React, { useState } from 'react';
import { doc, updateDoc, arrayRemove, arrayUnion, getDocs, collection, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Collapse, IconButton, Typography, Box, Table, TableBody, TableCell, TableHead, TableRow, TextField, Button, Divider, Alert, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const EditProvider = ({ providers, setMessage, fetchProviders }) => {
  const [editingProvider, setEditingProvider] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [openALS, setOpenALS] = useState(false);
  const [openBLS, setOpenBLS] = useState(false);
  const [oldEmail, setOldEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);

  const handleEdit = async (oldType, oldName, newName, newType) => {
    if (!newName.trim() || !newType) {
      setMessage('Please fill in all fields.');
      return;
    }

    try {
      const docRef = doc(db, 'hvac', 'GenericData', 'Providers', 'ProvidersList');
      await updateDoc(docRef, {
        [oldType]: arrayRemove(oldName)
      });
      await updateDoc(docRef, {
        [newType]: arrayUnion(newName)
      });
      setMessage(`Successfully updated ${oldName}.`);
      setEditingProvider(null);
      fetchProviders(); // Re-fetch providers after update
    } catch (error) {
      console.error('Error updating provider:', error);
      setMessage('Error updating provider. Please try again.');
    }
  };

  const handleBulkEmailUpdate = async () => {
    if (!oldEmail.trim() || !newEmail.trim()) {
      setMessage('Please fill in both old and new email addresses.');
      setUpdateStatus({ type: 'error', message: 'Please fill in both old and new email addresses.' });
      return;
    }

    if (oldEmail.toLowerCase() === newEmail.toLowerCase()) {
      setMessage('Old and new email addresses cannot be the same.');
      setUpdateStatus({ type: 'error', message: 'Old and new email addresses cannot be the same.' });
      return;
    }

    setIsUpdating(true);
    setUpdateStatus(null);
    setMessage('');

    try {
      const batch = writeBatch(db);
      let totalUpdates = 0;
      const updateStats = {
        users: 0,
        calls: 0,
        skills: 0,
        classes: 0,
        volunteer: 0
      };

      // Update users collection (field: email)
      const usersQuery = query(collection(db, 'users'), where('email', '==', oldEmail));
      const usersSnapshot = await getDocs(usersQuery);
      usersSnapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, { email: newEmail });
        updateStats.users++;
        totalUpdates++;
      });

      // Update CallData/Responses collection (field: Email)
      const callsQuery = query(
        collection(db, 'hvac', 'CallData', 'Responses'),
        where('Email', '==', oldEmail)
      );
      const callsSnapshot = await getDocs(callsQuery);
      callsSnapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, { Email: newEmail });
        updateStats.calls++;
        totalUpdates++;
      });

      // Update SkillData/Responses collection (field: Email)
      const skillsQuery = query(
        collection(db, 'hvac', 'SkillData', 'Responses'),
        where('Email', '==', oldEmail)
      );
      const skillsSnapshot = await getDocs(skillsQuery);
      skillsSnapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, { Email: newEmail });
        updateStats.skills++;
        totalUpdates++;
      });

      // Update ClassData/Responses collection (field: Email)
      const classesQuery = query(
        collection(db, 'hvac', 'ClassData', 'Responses'),
        where('Email', '==', oldEmail)
      );
      const classesSnapshot = await getDocs(classesQuery);
      classesSnapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, { Email: newEmail });
        updateStats.classes++;
        totalUpdates++;
      });

      // Update VolunteerData/Responses collection (field: Email)
      const volunteerQuery = query(
        collection(db, 'hvac', 'VolunteerData', 'Responses'),
        where('Email', '==', oldEmail)
      );
      const volunteerSnapshot = await getDocs(volunteerQuery);
      volunteerSnapshot.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, { Email: newEmail });
        updateStats.volunteer++;
        totalUpdates++;
      });

      // Commit all updates
      if (totalUpdates > 0) {
        await batch.commit();
        const statsMessage = `Successfully updated ${totalUpdates} record(s): Users (${updateStats.users}), Calls (${updateStats.calls}), Skills (${updateStats.skills}), Classes (${updateStats.classes}), Volunteer (${updateStats.volunteer})`;
        setMessage(statsMessage);
        setUpdateStatus({ type: 'success', message: statsMessage });
        setOldEmail('');
        setNewEmail('');
      } else {
        setMessage(`No records found with email: ${oldEmail}`);
        setUpdateStatus({ type: 'warning', message: `No records found with email: ${oldEmail}` });
      }
    } catch (error) {
      console.error('Error updating emails:', error);
      const errorMessage = `Error updating emails: ${error.message}`;
      setMessage(errorMessage);
      setUpdateStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box className="form-container" sx={{ padding: 2, borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h5" gutterBottom>Edit Providers</Typography>
      <div className="provider-section">
        <Typography variant="h6">
          <IconButton onClick={() => setOpenALS(!openALS)}>
            <ExpandMoreIcon />
          </IconButton>
          ALS Providers
        </Typography>
        <Collapse in={openALS}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.ALS && providers.ALS.length > 0 ? (
                providers.ALS.map((name) => (
                  <TableRow key={`ALS-${name}`}>
                    {editingProvider === `ALS-${name}` ? (
                      <>
                        <TableCell>
                          <TextField
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button onClick={() => handleEdit('ALS', name, displayName, 'ALS')}>Save</Button>
                          <Button onClick={() => setEditingProvider(null)}>Cancel</Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{name}</TableCell>
                        <TableCell>
                          <Button onClick={() => {
                            setEditingProvider(`ALS-${name}`);
                            setDisplayName(name);
                          }}>Edit</Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>No ALS Providers available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Collapse>
      </div>
      <div className="provider-section">
        <Typography variant="h6">
          <IconButton onClick={() => setOpenBLS(!openBLS)}>
            <ExpandMoreIcon />
          </IconButton>
          BLS Providers
        </Typography>
        <Collapse in={openBLS}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.BLS && providers.BLS.length > 0 ? (
                providers.BLS.map((name) => (
                  <TableRow key={`BLS-${name}`}>
                    {editingProvider === `BLS-${name}` ? (
                      <>
                        <TableCell>
                          <TextField
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button onClick={() => handleEdit('BLS', name, displayName, 'BLS')}>Save</Button>
                          <Button onClick={() => setEditingProvider(null)}>Cancel</Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{name}</TableCell>
                        <TableCell>
                          <Button onClick={() => {
                            setEditingProvider(`BLS-${name}`);
                            setDisplayName(name);
                          }}>Edit</Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>No BLS Providers available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Collapse>
      </div>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Email Update
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Update all records with an old email address to a new email address across all collections.
        </Typography>

        {updateStatus && (
          <Alert severity={updateStatus.type} sx={{ mb: 2 }}>
            {updateStatus.message}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
          <TextField
            label="Old Email Address"
            value={oldEmail}
            onChange={(e) => setOldEmail(e.target.value)}
            disabled={isUpdating}
            fullWidth
            type="email"
          />
          <TextField
            label="New Email Address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={isUpdating}
            fullWidth
            type="email"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkEmailUpdate}
            disabled={isUpdating || !oldEmail.trim() || !newEmail.trim()}
            sx={{ alignSelf: 'flex-start' }}
          >
            {isUpdating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              'Update All Records'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditProvider;